import { useState, useCallback } from "react";
import axios from "axios";
import { useAppContext } from "../../../AppContext";
import { useAuthentication } from "./useAuthentication";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import axiosInstance, { setAxiosToken } from "../helpers/axiosHelper";
import {
  extractFilenameFromHeaders,
  generateDefaultFilename,
  downloadCalendarFile,
} from "../../utilities/calendarUtility";
import { getErrorToast } from "../utilities/toastUtility";

export interface IUseCalendarDownloadReturn {
  downloadCalendar: (reservationId: number) => Promise<void>;
  isDownloading: boolean;
  error: Error | null;
}

/**
 * Hook for downloading calendar (.ics) files for reservations
 */
export const useCalendarDownload = (): IUseCalendarDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { selectedStudio } = useAppContext();
  const { currentUser } = useAuthentication();

  const downloadCalendar = useCallback(
    async (reservationId: number): Promise<void> => {
      // Validate studio is selected
      if (!selectedStudio?.id) {
        const error = new Error("Моля, изберете обект.");
        setError(error);
        getErrorToast("Моля, изберете обект.");
        return;
      }

      // Validate reservation ID
      if (!reservationId || reservationId <= 0) {
        const error = new Error("Невалидна резервация.");
        setError(error);
        getErrorToast("Невалидна резервация.");
        return;
      }

      setIsDownloading(true);
      setError(null);

      try {
        // Set auth token
        setAxiosToken(currentUser?.authToken);

        // Build endpoint URL with reservation ID and studio ID
        const url = `${endpointsMetadata.calendarDownload.reservation}/${reservationId}?studioId=${selectedStudio.id}`;

        // Make GET request with arraybuffer response type for file download
        const response = await axiosInstance.get(url, {
          responseType: "arraybuffer",
        });

        // Extract filename from response headers or generate default
        const filename =
          extractFilenameFromHeaders(response.headers) ||
          generateDefaultFilename(reservationId);

        // Trigger download
        downloadCalendarFile(response.data, filename);

        setError(null);
      } catch (err) {
        let errorMessage = "Възникна грешка при изтеглянето на календара.";

        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            errorMessage = "Нямате право да изтеглите този календар.";
          } else if (err.response?.status === 400) {
            // Try to extract error message from response
            if (err.response.data) {
              try {
                const decoder = new TextDecoder("utf-8");
                const text = decoder.decode(err.response.data);
                errorMessage = text || errorMessage;
              } catch {
                // Keep default error message
              }
            }
          } else if (err.response?.status === 404) {
            errorMessage = "Резервацията не е намерена.";
          } else if (err.response?.status === 500) {
            errorMessage = "Грешка на сървъра. Моля, опитайте отново по-късно.";
          }
        }

        const error = new Error(errorMessage);
        setError(error);
        getErrorToast(errorMessage);
      } finally {
        setIsDownloading(false);
      }
    },
    [selectedStudio, currentUser],
  );

  return {
    downloadCalendar,
    isDownloading,
    error,
  };
};
